
import { createSlice } from '@reduxjs/toolkit'
import type { Tariff, TableType } from '../../types'
import type { SessionTariffOptions } from '../../types/tariff.types'
import {
    createTariff,
    deleteTariff,
    fetchActiveTariffsByType,
    fetchSessionTariffOptions,
    fetchTariffs,
    reorderTariffMoveDown,
    reorderTariffMoveUp,
    updateTariff
} from "../thunks/tariffThunks.ts";


interface TariffsState {
    tariffs: Tariff[]
    activeTariffsByType: Record<TableType, Tariff[]>
    sessionTariffOptionsByType: Partial<Record<TableType, SessionTariffOptions>>
    selectedTariff: Tariff | null
    /** For Tariff Management screen: filter by table type (null = show first available type). */
    selectedTableTypeFilter: TableType | null
    /** Selected tariff id for row selection and move up/down. */
    selectedTariffId: number | null
    loading: boolean
    error: string | null
    lastFetch: number | null
}

const initialState: TariffsState = {
    tariffs: [],
    activeTariffsByType: {} as Record<TableType, Tariff[]>,
    sessionTariffOptionsByType: {},
    selectedTariff: null,
    selectedTableTypeFilter: null,
    selectedTariffId: null,
    loading: false,
    error: null,
    lastFetch: null
}

const tariffsSlice = createSlice({
    name: 'tariffs',
    initialState,
    reducers: {
        setSelectedTariff: (state, action) => {
            state.selectedTariff = action.payload
        },
        setSelectedTableTypeFilter: (state, action: { payload: TableType | null }) => {
            state.selectedTableTypeFilter = action.payload
        },
        setSelectedTariffId: (state, action: { payload: number | null }) => {
            state.selectedTariffId = action.payload
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        // Fetch Tariffs
        builder.addCase(fetchTariffs.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(fetchTariffs.fulfilled, (state, action) => {
            state.tariffs = action.payload
            state.loading = false
            state.lastFetch = Date.now()
        })
        builder.addCase(fetchTariffs.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        // Fetch Active Tariffs By Type
        builder.addCase(fetchActiveTariffsByType.fulfilled, (state, action) => {
            state.activeTariffsByType = action.payload
        })

        builder.addCase(fetchSessionTariffOptions.fulfilled, (state, action) => {
            state.sessionTariffOptionsByType[action.payload.tableType] = action.payload.options
        })

        // Create Tariff
        builder.addCase(createTariff.fulfilled, (state) => {
            // Tariffs will be refreshed by fetchTariffs
        })

        // Update Tariff
        builder.addCase(updateTariff.fulfilled, (state) => {
            // Tariffs will be refreshed by fetchTariffs
        })

        // Delete Tariff
        builder.addCase(deleteTariff.fulfilled, (state, action) => {
            state.tariffs = state.tariffs.filter(t => t.id !== action.payload)
        })

        // Reorder move up/down
        builder.addCase(reorderTariffMoveUp.rejected, (state, action) => {
            state.error = action.payload as string
        })
        builder.addCase(reorderTariffMoveDown.rejected, (state, action) => {
            state.error = action.payload as string
        })
    }
})

export const { setSelectedTariff, setSelectedTableTypeFilter, setSelectedTariffId, clearError } = tariffsSlice.actions
export default tariffsSlice.reducer